import { useEffect, useState, useMemo } from 'react';
import {
  Box,
  Flex,
  Text,
  Badge,
  HStack,
  VStack,
  Avatar,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  IconButton,
  Tooltip,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  ModalFooter,
  Button,
  useDisclosure,
  Divider,
} from '@chakra-ui/react';
import {
  ArrowsClockwise,
  Eye,
  Lightning,
  TrendDown,
  TrendUp,
  UsersThree,
  XCircle,
} from 'phosphor-react';
import moment from 'moment';
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { useActionToast } from '../../services/actionToast';
import { PageTitle } from '../../components/PageTitle';
import {
  DateField,
  Facet,
  FilterBar,
  FramedCard,
  FramedCardContent,
  PaginationFooter,
} from '../../components/ui';
import { DataSkeleton, EmptyState } from '../../components/states';
import { useFetch } from '../../hooks/useFetch';

interface PermissionLog {
  id: number;
  user_id: number;
  user_name: string;
  changed_by_id: number;
  changed_by_name: string;
  permission_id: number;
  permission_code: string;
  permission_name: string;
  action: string;
  scope: string;
  old_value: boolean | null;
  new_value: boolean | null;
  created_at: string;
}

const PermissionLogsPage = () => {
  const [filters, setFilters] = useState({
    action: '',
    scope: '',
    start_date: '',
    end_date: '',
    search: '',
  });
  const [paginationParams, setPaginationParams] = useState({ page: 1, per_page: 10 });
  const [selectedLog, setSelectedLog] = useState<PermissionLog | null>(null);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const toast = useActionToast();
  const hasActiveFilters = Object.values(filters).some(Boolean);
  const clearFilters = () => {
    setFilters({ action: '', scope: '', start_date: '', end_date: '', search: '' });
  };

  const {
    requestApi: fetchLogs,
    data: logsData,
    loading: loadingLogs,
  } = useFetch('/v1/admin/permissions/logs');

  useEffect(() => {
    loadLogs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const hasStartDate = filters.start_date !== '';
    const hasEndDate = filters.end_date !== '';

    if (hasStartDate !== hasEndDate) {
      toast({
        title: 'Filtro de data incompleto',
        description: 'Por favor, selecione tanto a data inicial quanto a data final',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    if (hasStartDate && hasEndDate && filters.start_date > filters.end_date) {
      toast({
        title: 'Datas inválidas',
        description: 'A data inicial não pode ser maior que a data final',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    if (paginationParams.page !== 1) {
      setPaginationParams((prev) => ({ ...prev, page: 1 }));
    } else {
      loadLogs({ page: 1 });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  useEffect(() => {
    loadLogs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paginationParams]);

  const loadLogs = async (overrides?: { page?: number; per_page?: number }) => {
    const params: any = {
      page: overrides?.page ?? paginationParams.page,
      per_page: overrides?.per_page ?? paginationParams.per_page,
    };

    // Só adiciona filtros se tiverem valor
    if (filters.action && filters.action !== '') {
      params.action = filters.action;
    }

    if (filters.scope && filters.scope !== '') {
      params.scope = filters.scope;
    }

    // Só envia as datas se ambas estiverem preenchidas
    if (
      filters.start_date &&
      filters.start_date !== '' &&
      filters.end_date &&
      filters.end_date !== ''
    ) {
      // Converter para ISO format com timezone
      params.start_date = new Date(`${filters.start_date}T00:00:00`).toISOString();
      params.end_date = new Date(`${filters.end_date}T23:59:59`).toISOString();
    }

    if (filters.search && filters.search !== '') {
      params.search = filters.search;
    }

    await fetchLogs('get', params);
  };

  const handleRefresh = () => {
    loadLogs();
    toast({
      title: 'Logs atualizados',
      status: 'success',
      duration: 2000,
    });
  };

  const handleViewDetails = (log: PermissionLog) => {
    setSelectedLog(log);
    onOpen();
  };

  const getActionIcon = (action: string) => {
    switch (action) {
      case 'grant':
        return <TrendUp size={16} weight="bold" />;
      case 'revoke':
        return <TrendDown size={16} weight="bold" />;
      case 'remove':
        return <XCircle size={16} weight="bold" />;
      default:
        return null;
    }
  };

  const getActionColor = (action: string) => {
    switch (action) {
      case 'grant':
        return 'success';
      case 'revoke':
        return 'warning';
      case 'remove':
        return 'destructive';
      default:
        return 'neutral';
    }
  };

  const getActionLabel = (action: string) => {
    switch (action) {
      case 'grant':
        return 'Concedida';
      case 'revoke':
        return 'Revogada';
      case 'remove':
        return 'Removida';
      default:
        return action;
    }
  };

  const getScopeLabel = (scope: string) => {
    return scope === 'group' ? 'Grupo' : 'Usuário';
  };

  // Calcular métricas e dados para gráficos
  const metrics = useMemo(() => {
    const logs: PermissionLog[] = logsData?.data || [];

    // Contar ações por tipo
    const actionCounts = {
      grant: logs.filter((l) => l.action === 'grant').length,
      revoke: logs.filter((l) => l.action === 'revoke').length,
      remove: logs.filter((l) => l.action === 'remove').length,
    };

    // Agrupar por data para o gráfico de linha
    const dateGroups: { [key: string]: { grant: number; revoke: number; remove: number } } = {};
    logs.forEach((log) => {
      const date = moment(log.created_at).format('DD/MM');
      if (!dateGroups[date]) {
        dateGroups[date] = { grant: 0, revoke: 0, remove: 0 };
      }
      if (log.action === 'grant') dateGroups[date].grant += 1;
      if (log.action === 'revoke') dateGroups[date].revoke += 1;
      if (log.action === 'remove') dateGroups[date].remove += 1;
    });

    const timelineData = Object.keys(dateGroups)
      .sort((a, b) => {
        const dateA = moment(a, 'DD/MM');
        const dateB = moment(b, 'DD/MM');
        return dateA.diff(dateB);
      })
      .map((date) => ({
        date,
        Concedidas: dateGroups[date].grant,
        Revogadas: dateGroups[date].revoke,
        Removidas: dateGroups[date].remove,
      }));

    // Dados para gráfico de barras
    const actionData = [
      { action: 'Concedidas', count: actionCounts.grant, color: '#48BB78' },
      { action: 'Revogadas', count: actionCounts.revoke, color: '#ED8936' },
      { action: 'Removidas', count: actionCounts.remove, color: '#F56565' },
    ];

    // Usuários únicos afetados
    const uniqueUsers = new Set(logs.map((l) => l.user_id)).size;

    // Total de mudanças
    const totalChanges = logs.length;

    return {
      actionCounts,
      timelineData,
      actionData,
      uniqueUsers,
      totalChanges,
    };
  }, [logsData]);

  const facets: Facet[] = [
    {
      key: 'action',
      label: 'Ação',
      icon: Lightning,
      anyLabel: 'Todas as ações',
      options: [
        { value: 'grant', label: 'Concedida' },
        { value: 'revoke', label: 'Revogada' },
        { value: 'remove', label: 'Removida' },
      ],
      values: filters.action ? [filters.action] : [],
      apply: ([next]) => setFilters({ ...filters, action: next ?? '' }),
    },
    {
      key: 'scope',
      label: 'Escopo',
      icon: UsersThree,
      anyLabel: 'Todos os escopos',
      options: [
        { value: 'group', label: 'Grupo' },
        { value: 'user', label: 'Usuário' },
      ],
      values: filters.scope ? [filters.scope] : [],
      apply: ([next]) => setFilters({ ...filters, scope: next ?? '' }),
    },
  ];

  return (
    <Box>
      <PageTitle description="Histórico de alterações de permissões do sistema.">
        Logs de Auditoria
      </PageTitle>

      <FilterBar
        facets={facets}
        hasActiveFilters={hasActiveFilters}
        onClear={clearFilters}
        totalCount={logsData?.pagination?.total_items}
        countLabel="registro"
        countLabelPlural="registros"
        search={{
          value: filters.search,
          onChange: (value) => setFilters({ ...filters, search: value }),
          label: 'Buscar por nome ou e-mail',
          placeholder: 'Nome ou e-mail',
        }}
        action={
          <Flex align="center" gap="8px">
            <Flex align="center" gap="4px">
              <Box w="150px">
                <DateField
                  data-testid="filter-start-date"
                  size="sm"
                  label="Data inicial"
                  value={filters.start_date}
                  onChange={(value) => setFilters({ ...filters, start_date: value })}
                />
              </Box>
              <Text fontSize="12px" color="content.faint">
                até
              </Text>
              <Box w="150px">
                <DateField
                  data-testid="filter-end-date"
                  size="sm"
                  label="Data final"
                  monthAnchor={filters.start_date}
                  value={filters.end_date}
                  onChange={(value) => setFilters({ ...filters, end_date: value })}
                />
              </Box>
            </Flex>
            <Tooltip label="Atualizar" openDelay={300}>
              <IconButton
                aria-label="Atualizar"
                variant="outline"
                size="sm"
                icon={<ArrowsClockwise size={14} />}
                onClick={handleRefresh}
              />
            </Tooltip>
          </Flex>
        }
      />

      {/* Métricas e Gráficos */}
      {!loadingLogs && logsData?.data && logsData.data.length > 0 && (
        <>
          {/* Cards de Métricas */}
          <SimpleGrid columns={{ base: 1, md: 3 }} spacing="12px" mb="16px">
            <Box bg="surface.base" p="16px" borderRadius="lg" boxShadow="card">
              <Stat>
                <StatLabel fontSize="sm">Total de Alterações</StatLabel>
                <StatNumber fontSize="2xl">{metrics.totalChanges}</StatNumber>
                <StatHelpText fontSize="xs">No período selecionado</StatHelpText>
              </Stat>
            </Box>

            <Box bg="surface.base" p="16px" borderRadius="lg" boxShadow="card">
              <Stat>
                <StatLabel fontSize="sm">Usuários Afetados</StatLabel>
                <StatNumber fontSize="2xl">{metrics.uniqueUsers}</StatNumber>
                <StatHelpText fontSize="xs">Usuários únicos</StatHelpText>
              </Stat>
            </Box>

            <Box bg="surface.base" p="16px" borderRadius="lg" boxShadow="card">
              <Stat>
                <StatLabel fontSize="sm">Permissões Concedidas</StatLabel>
                <StatNumber fontSize="2xl" color="state.success">
                  {metrics.actionCounts.grant}
                </StatNumber>
                <StatHelpText fontSize="xs">
                  {metrics.actionCounts.revoke} revogadas, {metrics.actionCounts.remove} removidas
                </StatHelpText>
              </Stat>
            </Box>
          </SimpleGrid>

          {/* Gráficos */}
          <SimpleGrid columns={{ base: 1, lg: 2 }} spacing="12px" mb="16px">
            {/* Gráfico de Barras - Ações por Tipo */}
            <Box bg="surface.base" p="20px" borderRadius="xl" boxShadow="card">
              <Text fontSize="md" fontWeight="600" mb={4}>
                Ações por Tipo
              </Text>
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={metrics.actionData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#F2F2F2" vertical={false} />
                  <XAxis dataKey="action" style={{ fontSize: '12px' }} />
                  <YAxis style={{ fontSize: '12px' }} />
                  <RechartsTooltip />
                  <Bar dataKey="count" fill="#1391C9" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </Box>

            {/* Gráfico de Linha - Timeline */}
            <Box bg="surface.base" p="20px" borderRadius="xl" boxShadow="card">
              <Text fontSize="md" fontWeight="600" mb={4}>
                Atividade ao Longo do Tempo
              </Text>
              <ResponsiveContainer width="100%" height={250}>
                <LineChart data={metrics.timelineData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#F2F2F2" vertical={false} />
                  <XAxis dataKey="date" style={{ fontSize: '12px' }} />
                  <YAxis style={{ fontSize: '12px' }} />
                  <RechartsTooltip />
                  <Legend />
                  <Line type="monotone" dataKey="Concedidas" stroke="#15B042" strokeWidth={2} />
                  <Line type="monotone" dataKey="Revogadas" stroke="#D9772E" strokeWidth={2} />
                  <Line type="monotone" dataKey="Removidas" stroke="#D63D3D" strokeWidth={2} />
                </LineChart>
              </ResponsiveContainer>
            </Box>
          </SimpleGrid>
        </>
      )}

      {/* Tabela de Logs */}
      <FramedCard>
        <FramedCardContent>
          <Box>
            {(loadingLogs || !logsData) && !logsData?.data?.length ? (
              <DataSkeleton
                variant="table"
                rows={8}
                columns={8}
                label="Carregando logs de permissão"
              />
            ) : (
              <Box overflowX="auto">
                <Table variant="serra">
                  <Thead>
                    <Tr bg="surface.frame">
                      <Th pl="16px">Data/Hora</Th>
                      <Th>Ação</Th>
                      <Th>Escopo</Th>
                      <Th>Usuário Afetado</Th>
                      <Th>Permissão</Th>
                      <Th>Alterado Por</Th>
                      <Th>Mudança</Th>
                      <Th width="80px" pr="16px" textAlign="right">
                        Ações
                      </Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {!logsData?.data || logsData.data.length === 0 ? (
                      <Tr>
                        <Td colSpan={8} py={8}>
                          <EmptyState
                            variant="bare"
                            title={
                              hasActiveFilters
                                ? 'Nenhum log para estes filtros'
                                : 'Nenhum log encontrado'
                            }
                            description={
                              hasActiveFilters
                                ? 'Remova os filtros para consultar outros eventos.'
                                : 'Não existem alterações de permissão registradas.'
                            }
                            action={
                              hasActiveFilters
                                ? { label: 'Limpar filtros', onClick: clearFilters }
                                : undefined
                            }
                          />
                        </Td>
                      </Tr>
                    ) : (
                      logsData.data.map((log: PermissionLog) => (
                        <Tr key={log.id}>
                          {/* Data/Hora */}
                          <Td>
                            <VStack align="start" spacing={0}>
                              <Text fontSize="xs" fontWeight="500">
                                {moment(log.created_at).format('DD/MM/YYYY')}
                              </Text>
                              <Text fontSize="xs" color="content.subtle">
                                {moment(log.created_at).format('HH:mm:ss')}
                              </Text>
                            </VStack>
                          </Td>

                          {/* Ação */}
                          <Td>
                            <Badge
                              variant={getActionColor(log.action)}
                              display="flex"
                              alignItems="center"
                              gap={1}
                              w="fit-content"
                            >
                              {getActionIcon(log.action)}
                              {getActionLabel(log.action)}
                            </Badge>
                          </Td>

                          {/* Escopo */}
                          <Td>
                            <Badge variant="neutral" fontSize="xs">
                              {getScopeLabel(log.scope)}
                            </Badge>
                          </Td>

                          {/* Usuário Afetado */}
                          <Td>
                            <HStack spacing={2}>
                              <Avatar size="xs" name={log.user_name} />
                              <VStack align="start" spacing={0}>
                                <Text fontSize="sm" fontWeight="500">
                                  {log.user_name}
                                </Text>
                                <Text fontSize="xs" color="content.subtle">
                                  ID: {log.user_id}
                                </Text>
                              </VStack>
                            </HStack>
                          </Td>

                          {/* Permissão */}
                          <Td>
                            <VStack align="start" spacing={0}>
                              <Text fontSize="sm" fontWeight="500">
                                {log.permission_name}
                              </Text>
                              <Text fontSize="xs" color="content.subtle" fontFamily="mono">
                                {log.permission_code}
                              </Text>
                            </VStack>
                          </Td>

                          {/* Alterado Por */}
                          <Td>
                            <HStack spacing={2}>
                              <Avatar size="xs" name={log.changed_by_name} />
                              <Text fontSize="sm">{log.changed_by_name}</Text>
                            </HStack>
                          </Td>

                          {/* Mudança */}
                          <Td>
                            <HStack spacing={1} fontSize="xs">
                              {log.old_value !== null && (
                                <Badge variant={log.old_value ? 'success' : 'destructive'}>
                                  {log.old_value ? 'Sim' : 'Não'}
                                </Badge>
                              )}
                              {log.old_value !== null && log.new_value !== null && (
                                <Text color="content.faint">→</Text>
                              )}
                              {log.new_value !== null && (
                                <Badge variant={log.new_value ? 'success' : 'destructive'}>
                                  {log.new_value ? 'Sim' : 'Não'}
                                </Badge>
                              )}
                            </HStack>
                          </Td>

                          {/* Ações */}
                          <Td>
                            <Tooltip label="Ver detalhes">
                              <IconButton
                                aria-label="Ver detalhes"
                                data-testid={`view-log-${log.id}`}
                                icon={<Eye size={16} />}
                                size="sm"
                                variant="ghost"
                                color="content.body"
                                onClick={() => handleViewDetails(log)}
                              />
                            </Tooltip>
                          </Td>
                        </Tr>
                      ))
                    )}
                  </Tbody>
                </Table>

                {logsData?.pagination && logsData.pagination.total_pages > 1 && (
                  <PaginationFooter
                    totalItems={logsData.pagination.total_items}
                    itemLabel="log"
                    itemLabelPlural="logs"
                    currentPage={logsData.pagination.current_page || 1}
                    totalPages={logsData.pagination.total_pages || 1}
                    onChange={(page) => setPaginationParams({ ...paginationParams, page })}
                  />
                )}
              </Box>
            )}
          </Box>
        </FramedCardContent>
      </FramedCard>

      {/* Modal de Detalhes do Log */}
      <Modal autoFocus={false} isOpen={isOpen} onClose={onClose} size="xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Detalhes do Log de Auditoria</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {selectedLog && (
              <VStack align="stretch" spacing={4}>
                {/* ID e Data */}
                <Box>
                  <Text fontSize="sm" fontWeight="bold" color="content.body" mb={2}>
                    Informações Gerais
                  </Text>
                  <HStack spacing={4}>
                    <VStack align="start" spacing={1} flex={1}>
                      <Text fontSize="xs" color="content.subtle">
                        ID do Log
                      </Text>
                      <Text fontSize="sm" fontWeight="500">
                        #{selectedLog.id}
                      </Text>
                    </VStack>
                    <VStack align="start" spacing={1} flex={1}>
                      <Text fontSize="xs" color="content.subtle">
                        Data e Hora
                      </Text>
                      <Text fontSize="sm" fontWeight="500">
                        {moment(selectedLog.created_at).format('DD/MM/YYYY [às] HH:mm:ss')}
                      </Text>
                    </VStack>
                  </HStack>
                </Box>

                <Divider />

                {/* Ação e Escopo */}
                <Box>
                  <Text fontSize="sm" fontWeight="bold" color="content.body" mb={2}>
                    Tipo de Alteração
                  </Text>
                  <HStack spacing={4}>
                    <VStack align="start" spacing={1} flex={1}>
                      <Text fontSize="xs" color="content.subtle">
                        Ação
                      </Text>
                      <Badge variant={getActionColor(selectedLog.action)} fontSize="sm">
                        {getActionIcon(selectedLog.action)} {getActionLabel(selectedLog.action)}
                      </Badge>
                    </VStack>
                    <VStack align="start" spacing={1} flex={1}>
                      <Text fontSize="xs" color="content.subtle">
                        Escopo
                      </Text>
                      <Badge variant="neutral" fontSize="sm">
                        {getScopeLabel(selectedLog.scope)}
                      </Badge>
                    </VStack>
                  </HStack>
                </Box>

                <Divider />

                {/* Usuário Afetado */}
                <Box>
                  <Text fontSize="sm" fontWeight="bold" color="content.body" mb={2}>
                    Usuário Afetado
                  </Text>
                  <HStack spacing={3}>
                    <Avatar size="sm" name={selectedLog.user_name} />
                    <VStack align="start" spacing={0}>
                      <Text fontSize="sm" fontWeight="500">
                        {selectedLog.user_name || 'Não informado'}
                      </Text>
                      <Text fontSize="xs" color="content.subtle">
                        ID: {selectedLog.user_id}
                      </Text>
                    </VStack>
                  </HStack>
                </Box>

                <Divider />

                {/* Permissão */}
                <Box>
                  <Text fontSize="sm" fontWeight="bold" color="content.body" mb={2}>
                    Permissão Alterada
                  </Text>
                  <VStack align="start" spacing={1}>
                    <Text fontSize="sm" fontWeight="500">
                      {selectedLog.permission_name || 'Não informado'}
                    </Text>
                    <Text fontSize="xs" color="content.subtle" fontFamily="mono">
                      Código: {selectedLog.permission_code || 'N/A'}
                    </Text>
                    <Text fontSize="xs" color="content.subtle">
                      ID: {selectedLog.permission_id}
                    </Text>
                  </VStack>
                </Box>

                <Divider />

                {/* Autor da Alteração */}
                <Box>
                  <Text fontSize="sm" fontWeight="bold" color="content.body" mb={2}>
                    Alterado Por
                  </Text>
                  <HStack spacing={3}>
                    <Avatar size="sm" name={selectedLog.changed_by_name} />
                    <VStack align="start" spacing={0}>
                      <Text fontSize="sm" fontWeight="500">
                        {selectedLog.changed_by_name || 'Não informado'}
                      </Text>
                      <Text fontSize="xs" color="content.subtle">
                        ID: {selectedLog.changed_by_id}
                      </Text>
                    </VStack>
                  </HStack>
                </Box>

                <Divider />

                {/* Mudança de Valor */}
                <Box>
                  <Text fontSize="sm" fontWeight="bold" color="content.body" mb={2}>
                    Mudança de Valor
                  </Text>
                  <HStack spacing={3} align="center">
                    <VStack spacing={1}>
                      <Text fontSize="xs" color="content.subtle">
                        Valor Anterior
                      </Text>
                      {selectedLog.old_value !== null ? (
                        <Badge
                          variant={selectedLog.old_value ? 'success' : 'destructive'}
                          fontSize="sm"
                        >
                          {selectedLog.old_value ? 'Permitido' : 'Negado'}
                        </Badge>
                      ) : (
                        <Badge variant="neutral" fontSize="sm">
                          Nenhum
                        </Badge>
                      )}
                    </VStack>
                    <Text color="content.faint" fontSize="2xl">
                      →
                    </Text>
                    <VStack spacing={1}>
                      <Text fontSize="xs" color="content.subtle">
                        Novo Valor
                      </Text>
                      {selectedLog.new_value !== null ? (
                        <Badge
                          variant={selectedLog.new_value ? 'success' : 'destructive'}
                          fontSize="sm"
                        >
                          {selectedLog.new_value ? 'Permitido' : 'Negado'}
                        </Badge>
                      ) : (
                        <Badge variant="neutral" fontSize="sm">
                          Nenhum
                        </Badge>
                      )}
                    </VStack>
                  </HStack>
                </Box>
              </VStack>
            )}
          </ModalBody>

          <ModalFooter>
            <Button onClick={onClose}>Fechar</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default PermissionLogsPage;
